<template>
  <main>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filter.search" v-on:keydown.enter="load"
              :placeholder="$t('keyword')" style="width: 240px"></b-form-input>
            <form-select :model="filter" class="mr-1 mb-1" attribute="type" :options="[
              {value: null, text: $t('selectType')},
              {value: 'personal', text: $t('personal')},
              {value: 'enterprise', text: $t('enterprise')},
              {value: 'all', text: $t('all')},
            ]" @change="load" />
            <form-select :model="filter" class="mr-1 mb-1" attribute="isActive" :options="[
              {value: null, text: $t('selectActive')},
              {value: 'true', text: $t('active')},
              {value: 'false', text: $t('inactive')},
            ]" @change="load" />
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="c-sorting">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button v-if="canEdit" @click="$refs.form.create()" class="btn btn-sm btn-success">
                  <font-awesome-icon icon="plus" />&nbsp;{{ $t('createTemplate') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="c-paging clearfix" v-if="totalDocs">
          <div class="c-paging__count">{{ $t('found', [totalDocs]) }}</div>
        </div>
      </div>

      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>{{ $t('code') }}</th>
                <th>{{ $t('name') }}</th>
                <th>{{ $t('type') }}</th>
                <th>{{ $t('fileName') }}</th>
                <th>{{ $t('variables') }}</th>
                <th>{{ $t('status') }}</th>
                <th>{{ $t('updatedBy') }}</th>
                <th class="text-right">{{ $t('actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item._id">
                <td>
                  <code>{{ item.code }}</code>
                  <span v-if="item.isSystem" class="badge badge-warning ml-1" :title="$t('systemTemplate')">
                    <i class="fa fa-shield-alt"></i>
                  </span>
                </td>
                <td>
                  <b>{{ item.name }}</b>
                  <div v-if="item.description" class="text-muted small">{{ item.description }}</div>
                </td>
                <td>
                  <span class="badge" :class="typeBadge(item.type)">{{ $t(item.type) }}</span>
                </td>
                <td>
                  <small class="text-muted">{{ item.fileName }}</small>
                  <div v-if="item.fileSize" class="small text-muted">{{ formatSize(item.fileSize) }}</div>
                </td>
                <td>{{ (item.variables || []).length }}</td>
                <td>
                  <span class="badge" :class="item.isActive ? 'badge-success' : 'badge-secondary'">
                    {{ item.isActive ? $t('active') : $t('inactive') }}
                  </span>
                </td>
                <td>
                  <div v-if="item.updatedBy" class="small">
                    {{ item.updatedBy.name }}
                    <div class="text-muted">{{ item.updateTime | moment('DD/MM/YYYY HH:mm') }}</div>
                  </div>
                </td>
                <td class="text-right">
                  <button class="btn btn-sm btn-outline-secondary mr-1 is-border-1"
                    @click="download(item)" :title="$t('downloadDocx')">
                    <i class="fa fa-download"></i>
                  </button>
                  <button v-if="canEdit" class="btn btn-sm btn-outline-primary mr-1 is-border-1"
                    @click="$refs.form.edit(item)" :title="$t('edit')">
                    <i class="fa fa-edit"></i>
                  </button>
                  <button v-if="canEdit" class="btn btn-sm is-border-1 mr-1"
                    :class="item.isActive ? 'btn-outline-warning' : 'btn-outline-success'"
                    @click="toggle(item)"
                    :title="item.isActive ? $t('deactivate') : $t('activate')">
                    <i class="fa" :class="item.isActive ? 'fa-pause' : 'fa-play'"></i>
                  </button>
                  <button v-if="canEdit && !item.isSystem" class="btn btn-sm btn-outline-danger is-border-1"
                    @click="remove(item)" :title="$t('delete')">
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="col-12" v-else-if="!loading">
        <div class="text-center py-5 text-muted">
          <i class="fa fa-file-alt fa-3x mb-3 d-block"></i>
          {{ $t('emptyTemplates') }}
        </div>
      </div>
    </div>

    <template-form-modal ref="form" @saved="load"></template-form-modal>
  </main>
</template>

<script>
import TemplateFormModal from './TemplateFormModal.vue';

export default {
  components: {TemplateFormModal},
  data() {
    return {
      service: '/kyc-contract-template',
      filter: {search: '', type: null, isActive: null, page: 1, limit: 50},
      items: [],
      totalDocs: 0,
      loading: false,
    };
  },
  computed: {
    canEdit() {
      const role = this.$store.state.user && this.$store.state.user.role;
      return ['owner', 'admin', 'tech', 'account'].includes(role);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    typeBadge(type) {
      return {personal: 'badge-info', enterprise: 'badge-primary', all: 'badge-secondary'}[type] || 'badge-light';
    },
    formatSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    },
    reset() {
      this.filter = {search: '', type: null, isActive: null, page: 1, limit: 50};
      this.load();
    },
    load() {
      this.loading = true;
      const params = {page: this.filter.page, limit: this.filter.limit};
      if (this.filter.search) params.search = this.filter.search;
      if (this.filter.type) params.type = this.filter.type;
      if (this.filter.isActive !== null && this.filter.isActive !== '') params.isActive = this.filter.isActive;
      this.$bgService.get(this.service, {params})
        .then(res => {
          this.items = res.data.docs || [];
          this.totalDocs = res.data.totalDocs || 0;
        })
        .catch(err => {
          this.$swal({title: this.$t('error'), text: (err.response && err.response.data && err.response.data.message) || err.message, icon: 'error'});
        })
        .finally(() => { this.loading = false; });
    },
    download(item) {
      this.$bgService.get(this.service + '/' + item._id)
        .then(res => {
          const url = res.data.fileDownloadUrl;
          if (url) window.open(url, '_blank');
        });
    },
    toggle(item) {
      this.$service.put(this.service + '/' + item._id + '/toggle')
        .then(() => this.load());
    },
    remove(item) {
      this.$swal({
        title: this.$t('confirmDeleteTitle'),
        text: this.$t('confirmDeleteText', [item.name]),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('delete'),
        cancelButtonText: this.$t('cancel'),
      }).then(result => {
        if (!result.value) return;
        this.$service.delete(this.service + '/' + item._id)
          .then(() => {
            this.$swal({title: this.$t('success'), icon: 'success', timer: 1500});
            this.load();
          });
      });
    },
  },
  i18n: {
    messages: {
      vi: {
        keyword: 'Tìm theo mã / tên / mô tả',
        selectType: 'Chọn loại',
        selectActive: 'Trạng thái',
        personal: 'Cá nhân',
        enterprise: 'Doanh nghiệp',
        all: 'Cả hai',
        active: 'Đang dùng',
        inactive: 'Tạm tắt',
        search: 'Tìm kiếm',
        createTemplate: 'Thêm mẫu hợp đồng',
        found: 'Tìm thấy {0} mẫu',
        code: 'Mã',
        name: 'Tên mẫu',
        type: 'Loại',
        fileName: 'File',
        variables: 'Số biến',
        status: 'Trạng thái',
        updatedBy: 'Sửa lần cuối',
        actions: 'Thao tác',
        edit: 'Sửa',
        delete: 'Xoá',
        activate: 'Kích hoạt',
        deactivate: 'Tạm tắt',
        downloadDocx: 'Tải file .docx',
        systemTemplate: 'Mẫu hệ thống — không thể xoá',
        emptyTemplates: 'Chưa có mẫu hợp đồng nào',
        confirmDeleteTitle: 'Xoá mẫu hợp đồng?',
        confirmDeleteText: 'Bạn sẽ tạm tắt mẫu "{0}". Có thể kích hoạt lại sau.',
        cancel: 'Huỷ',
        success: 'Thành công',
        error: 'Lỗi',
      },
      en: {
        keyword: 'Search by code / name / description',
        selectType: 'Type',
        selectActive: 'Status',
        personal: 'Personal',
        enterprise: 'Enterprise',
        all: 'Both',
        active: 'Active',
        inactive: 'Inactive',
        search: 'Search',
        createTemplate: 'New template',
        found: 'Found {0} templates',
        code: 'Code',
        name: 'Name',
        type: 'Type',
        fileName: 'File',
        variables: 'Variables',
        status: 'Status',
        updatedBy: 'Last edit',
        actions: 'Actions',
        edit: 'Edit',
        delete: 'Delete',
        activate: 'Activate',
        deactivate: 'Deactivate',
        downloadDocx: 'Download .docx',
        systemTemplate: 'System template — cannot delete',
        emptyTemplates: 'No templates yet',
        confirmDeleteTitle: 'Delete template?',
        confirmDeleteText: 'Template "{0}" will be deactivated. You can reactivate later.',
        cancel: 'Cancel',
        success: 'Success',
        error: 'Error',
      },
    },
  },
};
</script>
